/**
 * `/settings/privacy` — the per-contact refusals, the recording posture, and the
 * honest state of data export.
 *
 * Design §2.2 lists this screen as "data export, per-contact suppression,
 * do-not-record list"; §4.7 is where the recording half comes from. Four things
 * live here:
 *
 *  - **The recording posture and the disclosure snippet.** A legal notice, shown
 *    verbatim from `@crm/domain` — the same constant the server hands the macOS
 *    recorder, because two spellings of a legal notice is exactly what a shared
 *    constant exists to prevent.
 *  - **The suppression list**, filterable by flag, with per-flag removal. The
 *    previous version could only "Lift all", which is the one operation that
 *    cannot express "they are fine being emailed, they just do not want to be
 *    recorded".
 *  - **Adding a suppression**, which is what makes this screen a place a human
 *    can honour a request rather than only inspect one.
 *  - **Data export**, which has no procedure at this HEAD and says so.
 */

import { disclosureSnippet } from '@crm/domain';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  SegmentedControl,
  SkeletonRows,
} from '@crm/ui';
import { createFileRoute } from '@tanstack/react-router';
import { CopyIcon, MicOffIcon } from 'lucide-react';
import { useState } from 'react';
import {
  useClearSuppressionMutation,
  useContactListQuery,
  useSetSuppressionMutation,
  useSuppressionsQuery,
} from '../../../api/queries.ts';
import { Planned } from '../../../components/placeholders.tsx';
import {
  FLAG_EFFECT,
  FLAG_LABEL,
  flagPatch,
  SUPPRESSION_FLAGS,
  type SuppressionFlag,
} from '../../../components/privacy/suppression-controls.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { fullName } from '../../../lib/format.ts';

export const Route = createFileRoute('/_authed/settings/privacy')({
  component: PrivacySettings,
  errorComponent: RouteErrorPanel,
});

type FlagFilter = SuppressionFlag | 'all';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'noRecording', label: FLAG_LABEL.noRecording },
  { value: 'noEnrichment', label: FLAG_LABEL.noEnrichment },
  { value: 'noOutreach', label: FLAG_LABEL.noOutreach },
] as const satisfies readonly { value: FlagFilter; label: string }[];

/**
 * Adding a suppression.
 *
 * The contact picker is a native `<select>` over the first page of
 * `contacts.list`, not a search box: `contacts.list.q` is a 422 at this HEAD, so
 * a search field could only ever filter what had already been fetched. A select
 * that says how many contacts it is offering is the honest version of the same
 * control.
 */
function AddSuppression() {
  const contacts = useContactListQuery({ source: 'all' });
  const set = useSetSuppressionMutation();
  const [contactId, setContactId] = useState('');
  const [flag, setFlag] = useState<SuppressionFlag>('noRecording');
  const [reason, setReason] = useState('');

  const options = contacts.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Record a request</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Field
          id="suppress-contact"
          label="Who asked"
          required
          hint={
            contacts.isLoading
              ? 'Loading contacts…'
              : `Choosing from the ${options.length} contacts loaded so far.`
          }
        >
          {/* `w-full`: a native <select> sizes itself to its WIDEST OPTION, and
              the options here are "name · company" — so without a width it
              stretches the card past the viewport on a phone. */}
          <select
            id="suppress-contact"
            aria-describedby="suppress-contact-hint"
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={contactId}
            onChange={(event) => setContactId(event.target.value)}
          >
            <option value="">Select a contact…</option>
            {options.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {fullName(contact.firstName, contact.lastName)}
                {contact.companyName === null ? '' : ` · ${contact.companyName}`}
              </option>
            ))}
          </select>
        </Field>

        <Field id="suppress-flag" label="What they asked for">
          <SegmentedControl<SuppressionFlag>
            aria-label="Suppression flag"
            options={SUPPRESSION_FLAGS.map((value) => ({ value, label: FLAG_LABEL[value] }))}
            value={flag}
            onValueChange={(next) => setFlag(next ?? flag)}
          />
        </Field>
        <p className="text-xs text-muted-foreground">{FLAG_EFFECT[flag]}</p>

        <Field
          id="suppress-reason"
          label="Why (optional)"
          hint="Stored on the row so a colleague reading this list later knows it was a request, not a mistake."
        >
          <input
            id="suppress-reason"
            aria-describedby="suppress-reason-hint"
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={reason}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>

        <Button
          className="w-fit"
          disabled={contactId === '' || set.isPending}
          onClick={() => {
            // ONE flag key, per the PATCH semantics the contract spells out.
            set.mutate(
              {
                contactId,
                ...flagPatch(flag),
                ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
              },
              {
                onSuccess: () => {
                  setContactId('');
                  setReason('');
                },
              },
            );
          }}
        >
          {set.isPending ? 'Saving…' : 'Add suppression'}
        </Button>
      </CardContent>
    </Card>
  );
}

function SuppressionList() {
  const [filter, setFilter] = useState<FlagFilter>('all');
  const suppressions = useSuppressionsQuery(filter === 'all' ? null : filter);
  const clear = useClearSuppressionMutation();

  const items = suppressions.data?.items ?? [];

  return (
    <section aria-labelledby="suppression-heading">
      <div className="flex flex-wrap items-center gap-3">
        <h2 id="suppression-heading" className="text-sm font-semibold">
          People who asked us not to
        </h2>
        <SegmentedControl<FlagFilter>
          className="ml-auto"
          aria-label="Filter by flag"
          options={FILTERS}
          value={filter}
          onValueChange={(next) => setFilter(next ?? filter)}
        />
      </div>

      {suppressions.isLoading ? (
        <SkeletonRows rows={3} className="mt-3" />
      ) : suppressions.isError ? (
        <EmptyState kind="error" className="mt-3" title="Could not load the privacy list" />
      ) : items.length === 0 ? (
        <EmptyState
          className="mt-3"
          kind={filter === 'all' ? 'empty' : 'filtered'}
          title={filter === 'all' ? 'Nobody is suppressed' : `Nobody has “${FLAG_LABEL[filter]}”`}
          description="Record a request below, or set a flag from a contact's page."
        />
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {items.map((entry) => (
            <li
              key={entry.id}
              className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2"
            >
              {entry.noRecording ? (
                <MicOffIcon aria-hidden className="size-4 shrink-0 text-destructive" />
              ) : null}
              <span className="min-w-0 break-words text-sm font-medium">{entry.contactName}</span>

              {SUPPRESSION_FLAGS.filter((flag) => entry[flag]).map((flag) => (
                <span key={flag} className="inline-flex shrink-0 items-center gap-1">
                  <Badge variant={flag === 'noRecording' ? 'destructive' : 'muted'}>
                    {FLAG_LABEL[flag]}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1 text-xs text-muted-foreground"
                    aria-label={`Lift ${FLAG_LABEL[flag]} for ${entry.contactName}`}
                    disabled={clear.isPending}
                    onClick={() => clear.mutate({ contactId: entry.contactId, ...flagPatch(flag) })}
                  >
                    lift
                  </Button>
                </span>
              ))}

              {entry.reason === null ? null : (
                <span className="min-w-0 break-words text-xs text-muted-foreground">
                  {entry.reason}
                </span>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="ml-auto shrink-0"
                disabled={clear.isPending}
                onClick={() => clear.mutate({ contactId: entry.contactId })}
              >
                Lift all
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        Lifting a flag clears it; it does not always delete the row. A GDPR erasure keeps its record
        even when every flag is off — keyed on a hash that outlives the contact — so a deleted
        person stays suppressed forever. That is what the record is for.
      </p>
    </section>
  );
}

function PrivacySettings() {
  const [copied, setCopied] = useState(false);

  // The snippet is computed by `@crm/domain` from one constant, the same
  // function the server hands the macOS recorder. Two spellings of a legal
  // notice is exactly what the constant exists to prevent.
  const snippet = disclosureSnippet({});

  return (
    <div className="flex max-w-3xl flex-col gap-8">
      <section aria-labelledby="disclosure-heading">
        <h2 id="disclosure-heading" className="text-sm font-semibold">
          Recording disclosure
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Twelve US states require every party to consent to a recording, and California is one of
          them. The recorder never joins a call as a bot, which avoids the participant problem — it
          does not avoid the duty to say you are recording. Paste this into the invite or the
          meeting chat.
        </p>
        <blockquote className="mt-3 rounded-md border border-border bg-muted/40 p-3 text-sm">
          {snippet}
        </blockquote>
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => {
            void navigator.clipboard?.writeText(snippet).then(() => setCopied(true));
          }}
        >
          <CopyIcon aria-hidden className="size-3.5" />
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </section>

      <SuppressionList />

      <AddSuppression />

      <section aria-labelledby="export-heading">
        <h2 id="export-heading" className="text-sm font-semibold">
          Export your data
        </h2>
        <Planned
          className="mt-2"
          title="Download everything, or delete your account"
          description="A single archive of your contacts, timeline, meeting transcripts and where every detail came from — prepared in the background and emailed to you when it is ready — plus a way to close the account and take the data with you. Neither is available yet, and nothing is queued waiting for it."
        />
      </section>
    </div>
  );
}
